import React, { useState, useEffect, useRef } from 'react';
import { Send, Loader, Terminal } from 'lucide-react';
import { ChatMessage } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { sendMessage, listenToMessages, markMessagesAsRead } from '../api/chat';

interface TaskChatProps {
  connectionId: string;
  taskId: string;
  otherParticipantName?: string;
  /** Slimmer header + layout for embedding in workspace rail */
  compact?: boolean;
}

const TaskChat: React.FC<TaskChatProps> = ({ connectionId, taskId, otherParticipantName, compact = false }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Load messages and set up real-time listener
  useEffect(() => {
    if (!connectionId || !currentUser) return;

    setLoading(true);
    const unsubscribe = listenToMessages(connectionId, (newMessages) => {
      setMessages(newMessages);
      setLoading(false);

      // Mark messages as read if they're from the other person
      if (newMessages.length > 0) {
        const unreadFromOther = newMessages.filter(
          msg => msg.senderId !== currentUser.uid && !msg.read
        );
        if (unreadFromOther.length > 0) {
          markMessagesAsRead(connectionId, currentUser.uid).catch(console.error);
        }
      }
    }, 100); // Load last 100 messages

    return () => {
      unsubscribe();
    };
  }, [connectionId, currentUser]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || !currentUser || sending) return;

    const messageText = inputMessage.trim();
    setInputMessage('');
    setSending(true);

    try {
      await sendMessage(
        connectionId,
        currentUser.uid,
        currentUser.displayName || 'Anonymous',
        messageText
      );
    } catch (error: any) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + (error.message || 'Unknown error'));
      // Restore message on error
      setInputMessage(messageText);
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (timestamp: string | null | undefined) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-full text-tech-cyan font-mono text-sm bg-[#0a0f1d]">
        [ AUTHENTICATION_REQUIRED ]
      </div>
    );
  }

  return (
    <div 
      className="flex flex-col h-full min-h-0 bg-[#0a0f1d] relative font-sans"
      style={{
        backgroundImage: 'repeating-linear-gradient(transparent, transparent 31px, rgba(0, 217, 255, 0.05) 31px, rgba(0, 217, 255, 0.05) 32px), repeating-linear-gradient(90deg, transparent, transparent 31px, rgba(0, 217, 255, 0.02) 31px, rgba(0, 217, 255, 0.02) 32px)'
      }}
    >
      {/* Chat Header */}
      <div className={`${compact ? 'px-3 py-2.5' : 'px-5 py-4'} border-b border-tech-cyan/20 bg-[#0a0f1d]/80 backdrop-blur-md flex items-center gap-3 relative z-10 shadow-[0_4_20px_rgba(0,0,0,0.5)]`}>
        <div className={`${compact ? 'w-1 h-8' : 'w-1.5 h-10'} bg-tech-cyan rounded-full shadow-[0_0_10px_rgba(0,217,255,0.8)] shrink-0`} />
        <div className="min-w-0">
          <h3
            className={`font-bold text-tech-text uppercase tracking-wide flex items-center gap-2 ${
              compact ? 'text-xs' : 'text-sm'
            }`}
          >
            <Terminal
              className={`text-tech-cyan shrink-0 ${compact ? 'w-3 h-3' : 'w-4 h-4'}`}
            />
            <span>{compact ? 'Direct' : 'Discussion Board'}</span>
          </h3>
          <p
            className={`text-tech-cyan/70 font-mono uppercase tracking-wider truncate ${
              compact ? 'text-[9px]' : 'text-[10px] mt-1'
            }`}
          >
            {otherParticipantName || 'Collaborator'} &middot; {taskId.slice(0, 8)}...
          </p>
        </div>
      </div>

      {/* Messages Container */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5 custom-scrollbar relative z-10"
      >
        {!compact && (
        <div className="text-center w-full py-4 mb-4">
          <span className="bg-[#0a0f1d] border border-tech-cyan/20 text-tech-cyan/70 px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest">
            End-to-End Encrypted Session Open
          </span>
        </div>
        )}
        
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader className="w-6 h-6 text-tech-cyan animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-tech-cyan/50 h-[200px]">
             <span className="font-mono text-sm tracking-widest uppercase">Waiting for input...</span>
          </div>
        ) : (
          messages.map((message) => {
            const isCurrentUser = message.senderId === currentUser.uid;
            
            return (
              <div
                key={message.id}
                className={`w-full p-4 rounded-xl border backdrop-blur-md transition-all relative overflow-hidden group ${
                  isCurrentUser
                    ? 'bg-tech-cyan/5 border-tech-cyan/20 border-r-4 border-r-tech-cyan sm:w-[85%] ml-auto'
                    : 'bg-tech-accent/5 border-tech-accent/20 border-l-4 border-l-tech-accent sm:w-[85%] mr-auto'
                }`}
              >
                {/* Decorative glowing edge */}
                <div className={`absolute top-0 bottom-0 w-16 blur-[20px] opacity-[0.15] pointer-events-none ${isCurrentUser ? 'right-0 bg-tech-cyan' : 'left-0 bg-tech-accent'}`}></div>
                
                <div className="flex justify-between items-center mb-3">
                  <div className={`text-xs font-bold tracking-widest uppercase ${isCurrentUser ? 'text-tech-cyan' : 'text-tech-accent'}`}>
                    {isCurrentUser ? 'SYS.USER / You' : `EXT.USER / ${message.senderName}`}
                  </div>
                  <div className="text-[10px] text-tech-text-muted font-mono flex items-center gap-2">
                    {formatMessageTime(message.timestamp)}
                    {isCurrentUser && message.read && (
                      <span className="text-tech-cyan px-1 bg-tech-cyan/10 rounded">✓ READ</span>
                    )}
                  </div>
                </div>
                <p className="whitespace-pre-wrap break-words text-sm md:text-[15px] text-tech-text/90 leading-relaxed">
                  {message.message}
                </p>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-tech-cyan/20 bg-[#0a0f1d]/90 backdrop-blur-xl relative z-10">
        <form onSubmit={handleSendMessage} className="flex gap-3">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="[ TYPE COMMAND OR MESSAGE... ]"
            className="flex-1 px-5 py-3 bg-white/5 border border-tech-cyan/30 rounded-xl text-tech-text placeholder-tech-text-muted/40 focus:outline-none focus:border-tech-cyan focus:ring-1 focus:ring-tech-cyan font-mono text-sm shadow-inner"
            disabled={sending}
          />

          <button
            type="submit"
            disabled={!inputMessage.trim() || sending}
            className="px-6 py-3 bg-tech-cyan/10 border border-tech-cyan/50 text-tech-cyan font-bold rounded-xl hover:bg-tech-cyan hover:text-[#0a0f1d] hover:shadow-[0_0_15px_rgba(0,217,255,0.6)] transition-all disabled:opacity-50 disabled:hover:bg-tech-cyan/10 disabled:hover:text-tech-cyan disabled:hover:shadow-none flex items-center justify-center"
          >
            {sending ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TaskChat;
